import { cn } from '@/lib/cn'
import { COMMON_UNITS } from '@/lib/units'
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { useId } from 'react'

const FIELD_BASE =
  'w-full rounded-[var(--radius-field)] border bg-surface text-sm text-ink ' +
  'placeholder:text-ink-faint/70 transition-[border-color,box-shadow] duration-100 ' +
  'disabled:bg-surface-sunken disabled:text-ink-faint'

function fieldClasses(invalid: boolean | undefined): string {
  return cn(
    FIELD_BASE,
    invalid
      ? 'border-critical focus:border-critical'
      : 'border-line-strong hover:border-ink-faint focus:border-accent',
  )
}

export interface FieldProps {
  label?: ReactNode
  hint?: ReactNode
  error?: string | undefined
  required?: boolean
  className?: string
  children: (id: string, describedBy: string | undefined) => ReactNode
}

/** Wraps a control with its label, hint and error message. */
export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      {label ? (
        <label htmlFor={id} className="text-label font-medium text-ink-soft">
          {label}
          {required ? <span className="ml-0.5 text-critical">*</span> : null}
        </label>
      ) : null}
      {children(id, describedBy)}
      {error ? (
        <p id={errorId} className="text-micro text-critical">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-micro text-pretty text-ink-faint">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode
  hint?: ReactNode
  error?: string | undefined
  wrapperClassName?: string
}

export function Input({
  label,
  hint,
  error,
  wrapperClassName,
  className,
  ...rest
}: InputProps) {
  return (
    <Field label={label} hint={hint} error={error} className={wrapperClassName}>
      {(id, describedBy) => (
        <input
          {...rest}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(fieldClasses(Boolean(error)), 'h-10 px-3', className)}
        />
      )}
    </Field>
  )
}

/**
 * Free-text input with a datalist of likely values. Free text is deliberate:
 * units are open-ended, but the suggestions save typing the common ones.
 */
export function UnitInput({
  units = COMMON_UNITS,
  ...rest
}: InputProps & { units?: string[] }) {
  const listId = useId()
  return (
    <>
      <Input list={listId} {...rest} />
      <datalist id={listId}>
        {units.map((unit) => (
          <option key={unit} value={unit} />
        ))}
      </datalist>
    </>
  )
}

export interface NumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  label?: ReactNode
  hint?: ReactNode
  error?: string | undefined
  wrapperClassName?: string
  value: number | null
  onValueChange: (value: number | null) => void
  suffix?: ReactNode
}

export function NumberInput({
  label,
  hint,
  error,
  wrapperClassName,
  className,
  suffix,
  value,
  onValueChange,
  // Any decimal by default. A number input with no step is step=1 as far as the
  // browser is concerned, which makes 1.5 tbsp or 0.25 cup fail native validation
  // and silently blocks the form from submitting. Callers that only want whole
  // numbers still pass step={1}.
  step = 'any',
  ...rest
}: NumberInputProps) {
  return (
    <Field label={label} hint={hint} error={error} className={wrapperClassName}>
      {(id, describedBy) => (
        <div className="relative">
          <input
            {...rest}
            id={id}
            type="number"
            inputMode="decimal"
            step={step}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            value={value ?? ''}
            onChange={(event) => {
              const raw = event.target.value
              onValueChange(raw === '' ? null : Number(raw))
            }}
            className={cn(
              fieldClasses(Boolean(error)),
              'tabular h-10 px-3',
              suffix ? 'pr-10' : undefined,
              className,
            )}
          />
          {suffix ? (
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-ink-faint">
              {suffix}
            </span>
          ) : null}
        </div>
      )}
    </Field>
  )
}

export interface RangeFieldProps {
  label?: ReactNode
  hint?: ReactNode
  value: number
  min: number
  max: number
  step: number
  /** Formats the readout and the accessible value, e.g. 0.25 -> "0.25". */
  format?: (value: number) => string
  /** Fired continuously while dragging; the caller decides when to commit. */
  onValueChange: (value: number) => void
  disabled?: boolean
}

/** Digits the step actually asks for, so 0.01 does not read as "0.25.00". */
function stepDecimals(step: number): number {
  const dot = String(step).indexOf('.')
  return dot === -1 ? 0 : String(step).length - dot - 1
}

/**
 * A slider with a numeric readout. `onChange` fires on every step of a drag, so
 * callers that trigger requests should only commit on release.
 */
export function RangeField({
  label,
  hint,
  value,
  min,
  max,
  step,
  format,
  onValueChange,
  disabled,
}: RangeFieldProps) {
  // Stepping from a fractional minimum leaves floating point noise in the raw
  // value. Snap to the step's own precision so the readout and the value handed
  // up stay clean.
  const decimals = stepDecimals(step)
  const quantise = (current: number) => Number(current.toFixed(decimals))
  const display = format ?? ((current: number) => quantise(current).toFixed(decimals))

  return (
    <Field label={label} hint={hint}>
      {(id, describedBy) => (
        <div className="flex items-center gap-3">
          <input
            id={id}
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            disabled={disabled}
            aria-describedby={describedBy}
            aria-valuetext={display(value)}
            onChange={(event) => onValueChange(quantise(Number(event.target.value)))}
            className={cn(
              'h-1.5 min-w-0 flex-1 cursor-pointer rounded-full',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          />
          <span className="tabular w-14 shrink-0 text-right text-sm text-ink">
            {display(value)}
          </span>
        </div>
      )}
    </Field>
  )
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode
  hint?: ReactNode
  error?: string | undefined
  wrapperClassName?: string
}

export function Textarea({
  label,
  hint,
  error,
  wrapperClassName,
  className,
  ...rest
}: TextareaProps) {
  return (
    <Field label={label} hint={hint} error={error} className={wrapperClassName}>
      {(id, describedBy) => (
        <textarea
          {...rest}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(fieldClasses(Boolean(error)), 'px-3 py-2', className)}
        />
      )}
    </Field>
  )
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode
  hint?: ReactNode
  error?: string | undefined
  wrapperClassName?: string
  options: Array<{ value: string; label: string }>
  placeholder?: string
}

export function Select({
  label,
  hint,
  error,
  wrapperClassName,
  className,
  options,
  placeholder,
  ...rest
}: SelectProps) {
  return (
    <Field label={label} hint={hint} error={error} className={wrapperClassName}>
      {(id, describedBy) => (
        <select
          {...rest}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(fieldClasses(Boolean(error)), 'h-10 px-2', className)}
        >
          {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  )
}